import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, SYLLAPLUS_LOGO_B64 } from "../constants";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const navigate = useNavigate();

  // ── Core ──
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [fileView, setFileView] = useState("list");
  const [authView, setAuthView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");

  // ── Search ──
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [selectedResults, setSelectedResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeTagFilter, setActiveTagFilter] = useState("");

  // ── Files ──
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  // ── Drive ──
  const [driveAccounts, setDriveAccounts] = useState([]);
  const [activeDriveAccountId, setActiveDriveAccountId] = useState(null);
  const [driveFiles, setDriveFiles] = useState([]);
  const [importingDrive, setImportingDrive] = useState({});
  const [driveMessage, setDriveMessage] = useState("");

  // ── Redesign ──
  const [redesignFiles, setRedesignFiles] = useState([]);
  const [redesignParams, setRedesignParams] = useState({ current_weeks: 16, target_weeks: 16, student_level: "Undergraduate — Junior/Senior", focus: "", additional_notes: "", mode: "" });
  const [redesignResult, setRedesignResult] = useState(null);
  const [redesignLoading, setRedesignLoading] = useState(false);
  const [redesignError, setRedesignError] = useState("");
  const [refineInput, setRefineInput] = useState("");
  const [refineLoading, setRefineLoading] = useState(false);
  const [savingRedesign, setSavingRedesign] = useState(false);
  const [applyToTermId, setApplyToTermId] = useState("");
  const [applyingToCalendar, setApplyingToCalendar] = useState(false);
  const [redesignHistory, setRedesignHistory] = useState([]);
  const [redesignHistoryLoading, setRedesignHistoryLoading] = useState(false);
  const [savingToFolder, setSavingToFolder] = useState(false);
  const [saveToFolderMessage, setSaveToFolderMessage] = useState("");
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionIndex, setSessionIndex] = useState(-1);
  const [activeHistoryEntryId, setActiveHistoryEntryId] = useState(null);

  // ── Folders ──
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [showNewCourseForm, setShowNewCourseForm] = useState(false);
  const [newCourseName, setNewCourseName] = useState("");
  const [newCourseSubfolders, setNewCourseSubfolders] = useState([]);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [folderDefaults, setFolderDefaults] = useState([]);

  // ── Calendar ──
  const [terms, setTerms] = useState([]);
  const [activeTerm, setActiveTerm] = useState(null);
  const [allLectures, setAllLectures] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [showTermForm, setShowTermForm] = useState(false);
  const [termForm, setTermForm] = useState({ course_name: "", term_name: "", start_date: "", end_date: "", lecture_days: [], lecture_time_start: "10:00", lecture_time_end: "11:15", color: "#185FA5" });
  const [termLoading, setTermLoading] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [editTermForm, setEditTermForm] = useState({});
  const [editTermLoading, setEditTermLoading] = useState(false);
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [lectureEdit, setLectureEdit] = useState({ topic: "", notes: "", file_ids: [], date_type: "lecture" });
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [savingLecture, setSavingLecture] = useState(false);
  const [showApplySyllabus, setShowApplySyllabus] = useState(false);
  const [applySyllabusTermId, setApplySyllabusTermId] = useState("");
  const [applySyllabusFile, setApplySyllabusFile] = useState("");
  const [applySyllabusFolder, setApplySyllabusFolder] = useState("");
  const [applyingSyllabus, setApplyingSyllabus] = useState(false);
  const [applyMessage, setApplyMessage] = useState("");

  // ── File Manager ──
  const [sortBy, setSortBy] = useState("date_desc");
  const [copiedFileId, setCopiedFileId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadTargetFolder, setUploadTargetFolder] = useState(null);
  const [showNewFolderInModal, setShowNewFolderInModal] = useState(false);
  const [newFolderNameInModal, setNewFolderNameInModal] = useState("");
  const [folderSuggestions, setFolderSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [duplicateFolderId, setDuplicateFolderId] = useState(null);
  const [summarizingFileIds, setSummarizingFileIds] = useState([]);
  const [summarizingAll, setSummarizingAll] = useState(false);
  const [summarizeMessage, setSummarizeMessage] = useState("");
  const [retaggingFileIds, setRetaggingFileIds] = useState([]);
  const [retaggingAll, setRetaggingAll] = useState(false);
  const [retagMessage, setRetagMessage] = useState("");
  const [retagChanges, setRetagChanges] = useState(null);
  const [relatedFiles, setRelatedFiles] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [showRelatedPanel, setShowRelatedPanel] = useState(null);
  const [backfillingRelationships, setBackfillingRelationships] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState("");
  const [askQuestion, setAskQuestion] = useState("");
  const [askAnswer, setAskAnswer] = useState(null);
  const [askLoading, setAskLoading] = useState(false);
  const [askError, setAskError] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [trashedFiles, setTrashedFiles] = useState([]);
  const [trashedFolders, setTrashedFolders] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [trashMessage, setTrashMessage] = useState("");
  const [selectedTrashFiles, setSelectedTrashFiles] = useState([]);
  const [selectedTrashFolders, setSelectedTrashFolders] = useState([]);
  const [clipboard, setClipboard] = useState(null); // { type: 'file'|'folder', ids: [...], mode: 'copy'|'cut' }
  const [pasteCollision, setPasteCollision] = useState(null); // { type, existingId, filename/name, pasteAction }
  const [pasteFolderChoice, setPasteFolderChoice] = useState(null); // { folderId } — awaiting contents-vs-empty choice
  const [clipboardMessage, setClipboardMessage] = useState("");
  const [showGapAnalysisModal, setShowGapAnalysisModal] = useState(false);
  const [gapAnalysisStep, setGapAnalysisStep] = useState(1); // 1 = pick evidence files, 2 = pick syllabus file, 3 = results
  const [gapAnalysisFolderId, setGapAnalysisFolderId] = useState(null);
  const [gapAnalysisFiles, setGapAnalysisFiles] = useState([]);
  const [gapAnalysisSelectedIds, setGapAnalysisSelectedIds] = useState([]);
  const [gapAnalysisCourseFolderName, setGapAnalysisCourseFolderName] = useState("");
  const [loadingGapAnalysisFiles, setLoadingGapAnalysisFiles] = useState(false);
  const [gapAnalysisSyllabusFiles, setGapAnalysisSyllabusFiles] = useState([]);
  const [loadingSyllabusFiles, setLoadingSyllabusFiles] = useState(false);
  const [gapAnalysisSyllabusFileId, setGapAnalysisSyllabusFileId] = useState(null);
  const [gapAnalysisResult, setGapAnalysisResult] = useState(null);
  const [runningGapAnalysis, setRunningGapAnalysis] = useState(false);
  const [gapAnalysisError, setGapAnalysisError] = useState("");

  // ── Groups ──
  const [groups, setGroups] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupError, setGroupError] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitingMember, setInvitingMember] = useState(false);
  const [groupPosts, setGroupPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [creatingPost, setCreatingPost] = useState(false);
  const [postError, setPostError] = useState("");
  const [postDetail, setPostDetail] = useState(null);
  const [loadingPostDetail, setLoadingPostDetail] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [sharedFiles, setSharedFiles] = useState([]);
  const [loadingSharedFiles, setLoadingSharedFiles] = useState(false);
  const [groupFolders, setGroupFolders] = useState([]);
  const [copyingFileId, setCopyingFileId] = useState(null);
  const [sharedFilesMessage, setSharedFilesMessage] = useState("");
  const [summarizingSharedFileIds, setSummarizingSharedFileIds] = useState([]);
  const [deletingSharedFileId, setDeletingSharedFileId] = useState(null);
  const [downloadingSharedZip, setDownloadingSharedZip] = useState(false);

  // ── Theme ──
  const getAutoTheme = () => {
    const hour = new Date().getHours();
    return (hour >= 20 || hour < 6) ? "dark" : "light";
  };

  const [themeMode, setThemeMode] = useState("light");
  const [colorTheme, setColorTheme] = useState("default");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isVerified, setIsVerified] = useState(true);

  useEffect(() => {
    const applied = themeMode === "auto" ? getAutoTheme() : themeMode;
    document.documentElement.setAttribute("data-theme", applied);
  }, [themeMode]);

  useEffect(() => {
    if (colorTheme === "default") {
      document.documentElement.removeAttribute("data-color");
    } else {
      document.documentElement.setAttribute("data-color", colorTheme);
    }
  }, [colorTheme]);

  // Save preferences to backend whenever they change
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/user/preferences`, {
      method: "PUT",
      headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ theme_mode: themeMode, color_theme: colorTheme })
    });
  }, [themeMode, colorTheme, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto mode: update theme every minute
  useEffect(() => {
    if (themeMode !== "auto") return;
    const interval = setInterval(() => {
      const applied = getAutoTheme();
      document.documentElement.setAttribute("data-theme", applied);
    }, 60000);
    return () => clearInterval(interval);
  }, [themeMode]);

  // ── Effects ──
  useEffect(() => {
    if (token) { fetchUserProfile(); checkDriveStatus(); fetchTerms(); fetchFiles(); fetchFolders(); fetchGroups(); fetchPendingInvites(); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      checkDriveStatus(); setDriveMessage("✓ Google Drive connected!");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const authHeaders = { Authorization: `Bearer ${token}` };

  // ── Auth ──
  const validatePassword = (pwd) => {
    if (pwd.length < 8) return "At least 8 characters required";
    if (pwd.length > 72) return "Cannot exceed 72 characters";
    if (!/[a-z]/.test(pwd)) return "Add a lowercase letter";
    if (!/[A-Z]/.test(pwd)) return "Add an uppercase letter";
    if (!/\d/.test(pwd)) return "Add a number";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return "Add a special character";
    return "";
  };

  const getPasswordStrength = (pwd) => {
    if (!pwd) return "";
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;
    if (score <= 2) return "weak";
    if (score <= 3) return "fair";
    if (score <= 4) return "good";
    return "strong";
  };

  const handleSignup = async () => {
    setAuthError("");
    if (!firstName.trim()) { setAuthError("First name is required."); return; }
    if (!lastName.trim()) { setAuthError("Last name is required."); return; }
    if (!email.trim()) { setAuthError("Email address is required."); return; }
    const err = validatePassword(password);
    if (err) { setAuthError(err); return; }
    const res = await fetch(`${API}/signup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName }) });
    const data = await res.json();
    if (!res.ok) setAuthError(data.detail || "Signup failed");
    else { setAuthView("login"); setAuthError("Account created! Please log in."); }
  };

 const handleLogin = async () => {
    setAuthError("");
    if (!email.trim()) { setAuthError("Please enter your email address."); return; }
    if (!password.trim()) { setAuthError("Please enter your password."); return; }
    const form = new URLSearchParams();
    form.append("username", email); form.append("password", password);
    const res = await fetch(`${API}/login`, { method: "POST", body: form });
    const data = await res.json();
    if (!res.ok) {
      const msg = typeof data.detail === "string" ? data.detail : "Invalid email or password.";
      setAuthError(msg);
      return;
    }
    else {
      localStorage.setItem("token", data.access_token);
      setToken(data.access_token);
      await fetchUserProfile(data.access_token);
      navigate("/app/files");
    }
  };

  const handleForgotPassword = async (forgotEmail) => {
    setAuthError("");
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      return { success: res.ok, message: data.message || "Something went wrong. Please try again." };
    } catch {
      return { success: false, message: "Something went wrong. Please try again." };
    }
  };

  const handleResetPassword = async (token, newPassword) => {
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      const data = await res.json();
      return { success: res.ok, message: data.detail || data.message || "Something went wrong. Please try again." };
    } catch {
      return { success: false, message: "Something went wrong. Please try again." };
    }
  };

  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");

  const handleConfirmEmailChange = async (confirmToken) => {
    try {
      const res = await fetch(`${API}/auth/confirm-email-change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: confirmToken })
      });
      const data = await res.json();
      return { success: res.ok, message: data.detail || data.message || "Something went wrong. Please try again." };
    } catch {
      return { success: false, message: "Something went wrong. Please try again." };
    }
  };

  const handleVerifyEmail = async (verifyToken) => {
    try {
      const res = await fetch(`${API}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: verifyToken })
      });
      const data = await res.json();
      if (res.ok) setIsVerified(true);
      return { success: res.ok, message: data.detail || data.message || "Something went wrong. Please try again." };
    } catch {
      return { success: false, message: "Something went wrong. Please try again." };
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setVerificationMessage("");
    try {
      const res = await fetch(`${API}/auth/resend-verification`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      setVerificationMessage(data.message || "Something went wrong. Please try again.");
    } catch {
      setVerificationMessage("Something went wrong. Please try again.");
    }
    setResendingVerification(false);
  };

  const fetchUserProfile = async (overrideToken) => {
    try {
      const prefRes = await fetch(`${API}/user/preferences`, {
        headers: { Authorization: `Bearer ${overrideToken || token}` }
      });
      if (prefRes.ok) {
        const prefs = await prefRes.json();
        setCurrentUserId(prefs.id ?? null);
        setEmail(prefs.email || "");
        setThemeMode(prefs.theme_mode || "auto");
        setColorTheme(prefs.color_theme || "default");
        setFirstName(prefs.first_name || "");
        setLastName(prefs.last_name || "");
        setIsVerified(prefs.is_verified ?? true);
      }
    } catch {}
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(""); setResults([]); setFiles([]); setSearched(false);
    setThemeMode("light");
    setColorTheme("default");
    setCurrentUserId(null);
    document.documentElement.removeAttribute("data-theme");
    document.documentElement.removeAttribute("data-color");
    navigate("/");
  };

  // ── Files ──
  const fetchFiles = async () => {
    if (!token) return;
    const res = await fetch(`${API}/files`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const data = await res.json();
    setFiles(Array.isArray(data) ? data : []);
  };

  const handleIncomingFiles = async (fileArray) => {
    if (!fileArray.length) return;
    if (folders.length > 0) {
      setPendingFiles(fileArray); setShowUploadModal(true); setUploadTargetFolder(null);
    } else {
      await processUpload(fileArray, null);
    }
  };

  const handleUpload = async (e) => {
    const selected = Array.from(e.target.files);
    await handleIncomingFiles(selected);
    e.target.value = "";
  };

  const uploadSingleFile = async (file, folderId, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams();
    if (options.force) params.append("force", "true");
    if (options.replaceId) params.append("replace_id", options.replaceId);
    const url = `${API}/upload${params.toString() ? `?${params.toString()}` : ""}`;
    const res = await fetch(url, { method: "POST", headers: authHeaders, body: formData });
    const data = await res.json();
    if (data.duplicate_warning) return { status: "duplicate", data: data.duplicate_warning };
    if (data.message === "File uploaded successfully") {
      if (folderId && data.id) {
        await fetch(`${API}/folders/${folderId}/files`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ file_ids: [data.id] }) });
      }
      return { status: "uploaded" };
    }
    return { status: "skipped" };
  };

  const processUpload = async (selectedFiles, folderId) => {
    setUploading(true); setUploadProgress(0); setShowUploadModal(false);
    await runUploadQueue(selectedFiles, folderId, 0, 0);
  };

  const runUploadQueue = async (queue, folderId, uploadedSoFar, skippedSoFar) => {
    let uploaded = uploadedSoFar, skipped = skippedSoFar;
    for (let i = 0; i < queue.length; i++) {
      const file = queue[i];
      setUploadProgress(Math.round((i / queue.length) * 100));
      try {
        const result = await uploadSingleFile(file, folderId);
        if (result.status === "uploaded") {
          uploaded++;
        } else if (result.status === "duplicate") {
          setDuplicateWarning({ incomingFile: file, existingFile: result.data, remainingQueue: queue.slice(i + 1) });
          setDuplicateFolderId(folderId);
          setUploadMessage(`${uploaded} uploaded, ${skipped} skipped — waiting on duplicate decision`);
          return; // pause here; resumed via resolveDuplicate
        } else {
          skipped++;
        }
      } catch {
        skipped++;
      }
    }
    finishUpload(uploaded, skipped, folderId);
  };

  const finishUpload = (uploaded, skipped, folderId) => {
    setUploadProgress(100);
    setUploadMessage(`${uploaded} uploaded, ${skipped} skipped`);
    setUploading(false);
    fetchFiles();
    if (folderId) fetchFolders();
    setPendingFiles([]); setUploadTargetFolder(null);
  };

  const resolveDuplicate = async (choice) => {
    if (!duplicateWarning) return;
    const { incomingFile, existingFile, remainingQueue } = duplicateWarning;
    const folderId = duplicateFolderId;
    setDuplicateWarning(null);
    setUploading(true);
    let uploaded = 0, skipped = 0;
    try {
      if (choice === "keepBoth") {
        const result = await uploadSingleFile(incomingFile, folderId, { force: true });
        if (result.status === "uploaded") uploaded++; else skipped++;
      } else if (choice === "replace") {
        const result = await uploadSingleFile(incomingFile, folderId, { replaceId: existingFile.id });
        if (result.status === "uploaded") uploaded++; else skipped++;
      } else {
        skipped++;
      }
    } catch {
      skipped++;
    }
    await runUploadQueue(remainingQueue, folderId, uploaded, skipped);
  };

  const cancelDuplicateUpload = async () => {
    if (!duplicateWarning) return;
    const { remainingQueue } = duplicateWarning;
    const folderId = duplicateFolderId;
    setDuplicateWarning(null);
    await runUploadQueue(remainingQueue, folderId, 0, 1);
  };

  const handleDelete = async (id, filename) => {
    if (!window.confirm(`Move "${filename}" to Trash?`)) return;
    setFiles(prev => prev.filter(f => f.id !== id));
    setResults(prev => prev.filter(r => r.id !== id));
    await fetch(`${API}/files/${id}`, { method: "DELETE", headers: authHeaders });
    fetchFiles();
  };

  const handleBulkDelete = async (ids, refreshSearch = false) => {
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} file(s) to Trash?`)) return;
    setSelectedFiles([]); setSelectedResults([]);
    await fetch(`${API}/files-multiple`, { method: "DELETE", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ ids }) });
    await fetchFiles();
    if (refreshSearch) setResults(prev => prev.filter(r => !ids.includes(r.id)));
  };

  const fetchTrash = async () => {
    setLoadingTrash(true);
    try {
      const res = await fetch(`${API}/trash`, { headers: authHeaders });
      const data = await res.json();
      setTrashedFiles(data.files || []);
      setTrashedFolders(data.folders || []);
    } catch {
      setTrashedFiles([]);
      setTrashedFolders([]);
    }
    setLoadingTrash(false);
  };

  const handleRestoreFile = async (fileId) => {
    setTrashMessage("");
    try {
      const res = await fetch(`${API}/files/${fileId}/restore`, { method: "POST", headers: authHeaders });
      if (res.ok) {
        await fetchTrash();
        await fetchFiles();
        await fetchFolders();
      } else {
        const data = await res.json();
        setTrashMessage(data.detail || "Failed to restore file");
      }
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  const handlePermanentlyDeleteFile = async (fileId, filename) => {
    if (!window.confirm(`Permanently delete "${filename}"? This cannot be undone.`)) return;
    setTrashMessage("");
    try {
      const res = await fetch(`${API}/files/${fileId}/permanent`, { method: "DELETE", headers: authHeaders });
      if (res.ok) {
        await fetchTrash();
      } else {
        const data = await res.json();
        setTrashMessage(data.detail || "Failed to delete file");
      }
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  const handleRestoreFolder = async (folderId) => {
    setTrashMessage("");
    try {
      const res = await fetch(`${API}/folders/${folderId}/restore`, { method: "POST", headers: authHeaders });
      if (res.ok) {
        await fetchTrash();
        await fetchFiles();
        await fetchFolders();
      } else {
        const data = await res.json();
        setTrashMessage(data.detail || "Failed to restore folder");
      }
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  const handlePermanentlyDeleteFolder = async (folderId, name) => {
    if (!window.confirm(`Permanently delete "${name}" and everything inside it? This cannot be undone.`)) return;
    setTrashMessage("");
    try {
      const res = await fetch(`${API}/folders/${folderId}/permanent`, { method: "DELETE", headers: authHeaders });
      if (res.ok) {
        await fetchTrash();
      } else {
        const data = await res.json();
        setTrashMessage(data.detail || "Failed to delete folder");
      }
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  const toggleSelectTrashFile = (id) => setSelectedTrashFiles(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  const toggleSelectTrashFolder = (id) => setSelectedTrashFolders(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

  const handleBulkRestoreFiles = async () => {
    if (!selectedTrashFiles.length) return;
    setTrashMessage("");
    try {
      const res = await fetch(`${API}/files/restore-multiple`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ ids: selectedTrashFiles }) });
      if (res.ok) {
        setSelectedTrashFiles([]);
        await fetchTrash();
        await fetchFiles();
        await fetchFolders();
      } else {
        const data = await res.json();
        setTrashMessage(data.detail || "Failed to restore files");
      }
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  const handleBulkPermanentDeleteFiles = async () => {
    if (!selectedTrashFiles.length) return;
    if (!window.confirm(`Permanently delete ${selectedTrashFiles.length} file(s)? This cannot be undone.`)) return;
    setTrashMessage("");
    try {
      const res = await fetch(`${API}/files/permanent-delete-multiple`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ ids: selectedTrashFiles }) });
      if (res.ok) {
        setSelectedTrashFiles([]);
        await fetchTrash();
      } else {
        const data = await res.json();
        setTrashMessage(data.detail || "Failed to delete files");
      }
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  const handleBulkRestoreFolders = async () => {
    if (!selectedTrashFolders.length) return;
    setTrashMessage("");
    try {
      for (const folderId of selectedTrashFolders) {
        await fetch(`${API}/folders/${folderId}/restore`, { method: "POST", headers: authHeaders });
      }
      setSelectedTrashFolders([]);
      await fetchTrash();
      await fetchFiles();
      await fetchFolders();
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  const handleBulkPermanentDeleteFolders = async () => {
    if (!selectedTrashFolders.length) return;
    if (!window.confirm(`Permanently delete ${selectedTrashFolders.length} folder(s) and everything inside them? This cannot be undone.`)) return;
    setTrashMessage("");
    try {
      for (const folderId of selectedTrashFolders) {
        await fetch(`${API}/folders/${folderId}/permanent`, { method: "DELETE", headers: authHeaders });
      }
      setSelectedTrashFolders([]);
      await fetchTrash();
    } catch {
      setTrashMessage("Something went wrong. Please try again.");
    }
  };

  // ── Clipboard (Copy/Cut/Paste via keyboard) ──
  const clipboardCopyFiles = (ids) => { setClipboard({ type: "file", ids, mode: "copy" }); setClipboardMessage(`${ids.length} file${ids.length !== 1 ? "s" : ""} copied`); };
  const clipboardCutFiles = (ids) => { setClipboard({ type: "file", ids, mode: "cut" }); setClipboardMessage(`${ids.length} file${ids.length !== 1 ? "s" : ""} cut`); };
  const clipboardCopyFolder = (id) => { setClipboard({ type: "folder", ids: [id], mode: "copy" }); setClipboardMessage("Folder copied"); };
  const clipboardCutFolder = (id) => { setClipboard({ type: "folder", ids: [id], mode: "cut" }); setClipboardMessage("Folder cut"); };

  const performFilePaste = async (targetFolderId) => {
    if (!clipboard || clipboard.type !== "file") return;
    setClipboardMessage("");
    for (const fileId of clipboard.ids) {
      const file = files.find(f => f.id === fileId);
      const filename = file?.filename;
      if (!filename) continue;

      const collisionRes = await fetch(`${API}/files/check-collision`, {
        method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ filename, target_folder_id: targetFolderId })
      });
      const collisionData = await collisionRes.json();

      if (collisionData.collision) {
        setPasteCollision({ itemType: "file", fileId, filename, existingId: collisionData.existing_file_id, targetFolderId, mode: clipboard.mode });
        return; // handle one collision at a time; resolvePasteCollision continues the rest
      }

      if (clipboard.mode === "copy") {
        await fetch(`${API}/files/${fileId}/duplicate`, {
          method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ target_folder_id: targetFolderId })
        });
      } else {
        await fetch(`${API}/files/${fileId}/move`, {
          method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ target_folder_id: targetFolderId })
        });
      }
    }
    if (clipboard.mode === "cut") setClipboard(null);
    await fetchFiles();
    await fetchFolders();
    setClipboardMessage("Paste complete");
  };

  const resolvePasteCollision = async (action) => {
    if (!pasteCollision) return;
    const { fileId, filename, existingId, targetFolderId, mode } = pasteCollision;
    setPasteCollision(null);

    if (action === "cancel") {
      setClipboard(null);
      return;
    }

    if (action === "overwrite") {
      await fetch(`${API}/files/${existingId}/permanent`, { method: "DELETE", headers: authHeaders });
      if (mode === "copy") {
        await fetch(`${API}/files/${fileId}/duplicate`, {
          method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ target_folder_id: targetFolderId, new_filename: filename })
        });
      } else {
        await fetch(`${API}/files/${fileId}/move`, {
          method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ target_folder_id: targetFolderId })
        });
      }
    } else if (action === "duplicate") {
      if (mode === "copy") {
        await fetch(`${API}/files/${fileId}/duplicate`, {
          method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ target_folder_id: targetFolderId })
        });
      } else {
        // Cut+Duplicate doesn't quite make sense (moving can't "keep both"), so treat as move anyway
        await fetch(`${API}/files/${fileId}/move`, {
          method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ target_folder_id: targetFolderId })
        });
      }
    }

    if (mode === "cut") setClipboard(null);
    await fetchFiles();
    await fetchFolders();
  };

  const initiateFolderPaste = (targetParentId) => {
    if (!clipboard || clipboard.type !== "folder") return;
    if (clipboard.mode === "cut") {
      performFolderMove(clipboard.ids[0], targetParentId);
    } else {
      setPasteFolderChoice({ folderId: clipboard.ids[0], targetParentId });
    }
  };

  const performFolderMove = async (folderId, targetParentId) => {
    const res = await fetch(`${API}/folders/${folderId}/move`, {
      method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ target_parent_id: targetParentId })
    });
    if (res.ok) {
      setClipboard(null);
      await fetchFolders();
      setClipboardMessage("Folder moved");
    } else {
      const data = await res.json();
      setClipboardMessage(data.detail || "Failed to move folder");
    }
  };

  const resolveFolderPasteChoice = async (includeContents) => {
    if (!pasteFolderChoice) return;
    const { folderId, targetParentId } = pasteFolderChoice;
    setPasteFolderChoice(null);
    const res = await fetch(`${API}/folders/${folderId}/duplicate`, {
      method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ include_contents: includeContents, target_parent_id: targetParentId })
    });
    if (res.ok) {
      await fetchFolders();
      await fetchFiles();
      setClipboardMessage("Folder copied");
    } else {
      const data = await res.json();
      setClipboardMessage(data.detail || "Failed to copy folder");
    }
  };

  // ── Learning Objective Gap Analysis ──
  const openGapAnalysisModal = async (folderId) => {
    setShowGapAnalysisModal(true);
    setGapAnalysisStep(1);
    setGapAnalysisFolderId(folderId);
    setGapAnalysisResult(null);
    setGapAnalysisError("");
    setGapAnalysisSyllabusFileId(null);
    setLoadingGapAnalysisFiles(true);
    try {
      const res = await fetch(`${API}/folders/${folderId}/gap-analysis/files`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) {
        setGapAnalysisFiles(data.files || []);
        setGapAnalysisSelectedIds((data.files || []).map(f => f.id));
        setGapAnalysisCourseFolderName(data.course_folder_name || "");
      } else {
        setGapAnalysisError(data.detail || "Failed to load files");
      }
    } catch {
      setGapAnalysisError("Something went wrong. Please try again.");
    }
    setLoadingGapAnalysisFiles(false);
  };

  const closeGapAnalysisModal = () => {
    setShowGapAnalysisModal(false);
    setGapAnalysisStep(1);
    setGapAnalysisFiles([]);
    setGapAnalysisSelectedIds([]);
    setGapAnalysisSyllabusFiles([]);
    setGapAnalysisSyllabusFileId(null);
    setGapAnalysisResult(null);
    setGapAnalysisError("");
  };

  const toggleGapAnalysisFile = (fileId) => {
    setGapAnalysisSelectedIds(prev => prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]);
  };

  const goToGapAnalysisStep2 = async () => {
    setGapAnalysisError("");
    setLoadingSyllabusFiles(true);
    try {
      const res = await fetch(`${API}/folders/${gapAnalysisFolderId}/gap-analysis/syllabus-files`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) {
        setGapAnalysisSyllabusFiles(data.files || []);
        setGapAnalysisStep(2);
      } else {
        setGapAnalysisError(data.detail || "Failed to load syllabus files");
      }
    } catch {
      setGapAnalysisError("Something went wrong. Please try again.");
    }
    setLoadingSyllabusFiles(false);
  };

  const backToGapAnalysisStep1 = () => {
    setGapAnalysisStep(1);
    setGapAnalysisError("");
  };

  const runGapAnalysis = async () => {
    if (!gapAnalysisSyllabusFileId) return;
    setRunningGapAnalysis(true);
    setGapAnalysisError("");
    setGapAnalysisResult(null);
    try {
      const res = await fetch(`${API}/folders/${gapAnalysisFolderId}/gap-analysis`, {
        method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ file_ids: gapAnalysisSelectedIds, syllabus_file_id: gapAnalysisSyllabusFileId })
      });
      const data = await res.json();
      if (res.ok) {
        setGapAnalysisResult(data);
        setGapAnalysisStep(3);
      } else {
        setGapAnalysisError(data.detail || "Failed to run analysis");
      }
    } catch {
      setGapAnalysisError("Something went wrong. Please try again.");
    }
    setRunningGapAnalysis(false);
  };

  const downloadZip = async (ids) => {
    const res = await fetch(`${API}/download-zip`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ ids }) });
    if (!res.ok) { alert("Download failed"); return; }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = "syllaplus_files.zip";
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); window.URL.revokeObjectURL(url);
  };

  const handleDownloadAll = () => downloadZip(files.map(f => f.id));
  const handleBulkDownload = (items) => downloadZip(items.map(i => i.id));
  const toggleSelectFile = (id) => setSelectedFiles(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  const toggleSelectResult = (id) => setSelectedResults(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  // ── Search ──
  const getFolderFileIds = (folderId) => {
    const ids = new Set();
    const findAndCollect = (folderList) => {
      for (const f of folderList) {
        if (f.id === folderId) {
          (f._file_ids || []).forEach(id => ids.add(id));
          (f.children || []).forEach(child => { (child._file_ids || []).forEach(id => ids.add(id)); });
          return true;
        }
        if (f.children?.length && findAndCollect(f.children)) return true;
      }
      return false;
    };
    findAndCollect(folders);
    return ids;
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    navigate(`/app/search?q=${encodeURIComponent(query)}`);
    setSelectedResults([]); setActiveTagFilter("");
    const [exactRes, semanticRes] = await Promise.all([
      fetch(`${API}/search?q=${encodeURIComponent(query)}`, { headers: authHeaders }),
      fetch(`${API}/semantic-search?q=${encodeURIComponent(query)}`, { headers: authHeaders })
    ]);
    const exactData = exactRes.ok ? await exactRes.json() : { results: [] };
    const semanticData = semanticRes.ok ? await semanticRes.json() : { results: [] };
    const seen = new Set(); const combined = [];
    for (const r of (exactData.results || [])) { if (!seen.has(r.id)) { seen.add(r.id); combined.push({ ...r, matchType: "exact" }); } }
    for (const r of (semanticData.results || [])) { if (!seen.has(r.id)) { seen.add(r.id); combined.push({ ...r, matchType: "semantic" }); } }
    if (activeFolderId) {
      const folderFileIds = getFolderFileIds(activeFolderId);
      setResults(combined.filter(r => folderFileIds.has(r.id)));
    } else {
      setResults(combined);
    }
    setSearched(true);
  };

  const fetchSuggestions = async (value) => {
    if (value.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await fetch(`${API}/suggestions?q=${encodeURIComponent(value)}`, { headers: authHeaders });
      const data = await res.json();
      setSuggestions(data.suggestions || []);
      setShowSuggestions((data.suggestions || []).length > 0);
    } catch { setSuggestions([]); setShowSuggestions(false); }
  };

  // ── Drive ──
  const checkDriveStatus = async () => {
    try {
      const res = await fetch(`${API}/drive/status`, { headers: authHeaders });
      const data = await res.json();
      const accounts = data.accounts || [];
      setDriveAccounts(accounts);
      setActiveDriveAccountId(prev => {
        if (prev && accounts.some(a => a.id === prev)) return prev;
        return accounts.length > 0 ? accounts[0].id : null;
      });
    } catch {
      setDriveAccounts([]);
      setActiveDriveAccountId(null);
    }
  };

  const fetchDriveFiles = async (tokenId) => {
    const targetId = tokenId ?? activeDriveAccountId;
    if (!targetId) { setDriveFiles([]); return; }
    try {
      const res = await fetch(`${API}/drive/files?token_id=${targetId}`, { headers: authHeaders });
      const data = await res.json();
      setDriveFiles(data.files || []);
    } catch { setDriveFiles([]); }
  };

  const connectGoogleDrive = async () => {
    const res = await fetch(`${API}/auth/google`, { headers: authHeaders });
    const data = await res.json();
    window.location.href = data.auth_url;
  };

  const disconnectDriveAccount = async (tokenId) => {
    try {
      await fetch(`${API}/drive/disconnect/${tokenId}`, { method: "DELETE", headers: authHeaders });
      await checkDriveStatus();
      if (activeDriveAccountId === tokenId) setDriveFiles([]);
    } catch {
      setDriveMessage("Failed to disconnect account");
    }
  };

  const importDriveFile = async (fileId, filename) => {
    if (!activeDriveAccountId) { setDriveMessage("No Google account selected"); return; }
    setImportingDrive(prev => ({ ...prev, [fileId]: true }));
    try { await fetch(`${API}/drive/import/${fileId}?token_id=${activeDriveAccountId}`, { method: "POST", headers: authHeaders }); setDriveMessage(`✓ ${filename} imported successfully`); fetchFiles(); }
    catch { setDriveMessage(`Failed to import ${filename}`); }
    setImportingDrive(prev => ({ ...prev, [fileId]: false }));
  };

  // ── Redesign ──
  const handleRedesign = async () => {
    if (redesignFiles.length === 0) { setRedesignError("Please select at least one file."); return; }
    if (!redesignParams.focus.trim()) { setRedesignError("Please describe the course focus."); return; }
    setRedesignLoading(true); setRedesignError(""); setRedesignResult(null);
    try {
      const res = await fetch(`${API}/course-redesign`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ ...redesignParams, selected_file_ids: redesignFiles }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Redesign failed");
      setRedesignResult(data);
      const newSession = [data];
      setSessionHistory(newSession); setSessionIndex(0); setActiveHistoryEntryId(null);
      saveRedesignToHistory(data, redesignParams, newSession, 0, null);
    } catch (err) { setRedesignError(err.message || "Something went wrong."); }
    setRedesignLoading(false);
  };

  const handleRefine = async () => {
    if (!refineInput.trim()) return;
    setRefineLoading(true); setRedesignError("");
    try {
      const res = await fetch(`${API}/course-redesign/refine`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ current_syllabus: redesignResult, feedback: refineInput }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Refinement failed");
      setRedesignResult(data); setRefineInput("");
      setSessionHistory(prev => {
        const next = [...prev.slice(0, sessionIndex + 1), data];
        const nextIndex = next.length - 1;
        setSessionIndex(nextIndex);
        saveRedesignToHistory(data, redesignParams, next, nextIndex, activeHistoryEntryId);
        return next;
      });
    } catch (err) { setRedesignError(err.message || "Something went wrong."); }
    setRefineLoading(false);
  };

  const toggleRedesignFile = (id) => setRedesignFiles(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

  const loadRedesignHistory = async () => {
    setRedesignHistoryLoading(true);
    try {
      const res = await fetch(`${API}/course-redesign/history`, { headers: authHeaders });
      const data = await res.json();
      setRedesignHistory(Array.isArray(data) ? data : []);
    } catch { setRedesignHistory([]); }
    setRedesignHistoryLoading(false);
  };

  const fetchRedesignHistoryEntry = async (id) => {
    const res = await fetch(`${API}/course-redesign/history/${id}`, { headers: authHeaders });
    const data = await res.json();
    if (data.session_results?.length > 0) {
      setSessionHistory(data.session_results);
      setSessionIndex(data.session_index ?? data.session_results.length - 1);
      setActiveHistoryEntryId(id);
    }
    return data;
  };

  const saveRedesignToHistory = async (result, params, newSessionHistory, newSessionIndex, entryId = null) => {
    if (entryId) {
      await fetch(`${API}/course-redesign/history/${entryId}`, { method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ course_title: result.course_title || "Untitled", result, params, session_results: newSessionHistory, session_index: newSessionIndex }) });
    } else {
      const res = await fetch(`${API}/course-redesign/history`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ course_title: result.course_title || "Untitled", result, params, session_results: newSessionHistory, session_index: newSessionIndex }) });
      const data = await res.json();
      if (data.id) setActiveHistoryEntryId(data.id);
    }
  };

  const deleteRedesignHistoryEntry = async (id) => {
    await fetch(`${API}/course-redesign/history/${id}`, { method: "DELETE", headers: authHeaders });
    setRedesignHistory(prev => prev.filter(e => e.id !== id));
  };

  const handleSaveSyllabusToFolder = async (result, folderId) => {
    const blob = await downloadRedesignAsBlob(result);
    const base64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(blob);
    });
    const filename = `${(result.course_title || "syllabus").replace(/\s+/g, "_")}_SyllaPlus.docx`;
    const res = await fetch(`${API}/save-syllabus-to-folder`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ filename, file_base64: base64, folder_id: folderId, course_title: result.course_title || "Syllabus" }) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Failed to save");
    await fetch(`${API}/save-syllabus-data`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ folder_id: folderId, file_id: data.file_id, structured_json: JSON.stringify(result) }) });
    await fetchFiles(); await fetchFolders();
    return data;
  };

  const handleSaveRedesign = async () => {
    if (!redesignResult) return;
    setSavingRedesign(true);
    try {
      const res = await fetch(`${API}/save-redesign`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ redesign: redesignResult }) });
      const data = await res.json();
      if (res.ok) alert(`✓ "${data.filename}" saved to My Files!`);
      else alert(data.detail || "Failed to save");
    } catch { alert("Something went wrong. Please try again."); }
    setSavingRedesign(false);
  };

  const handleApplyRedesignToCalendar = async () => {
    if (!applyToTermId || !redesignResult) return;
    setApplyingToCalendar(true);
    try {
      const res = await fetch(`${API}/terms/${applyToTermId}/apply-redesign`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify(redesignResult) });
      if (res.ok) { alert("✓ Redesign applied to calendar!"); setApplyToTermId(""); }
      else { const data = await res.json(); alert(data.detail || "Failed to apply"); }
    } catch { alert("Something went wrong."); }
    setApplyingToCalendar(false);
  };

  const downloadRedesignAsBlob = async (result) => {
    const { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
      AlignmentType, BorderStyle, WidthType, ShadingType,
      Header, Footer, ImageRun, PageNumber, TabStopType, TabStopPosition } = await import('docx');
    const docxLogoRaw = atob(SYLLAPLUS_LOGO_B64);
    const docxLogoBytes = new Uint8Array(docxLogoRaw.length);
    for (let i = 0; i < docxLogoRaw.length; i++) docxLogoBytes[i] = docxLogoRaw.charCodeAt(i);
    const border = { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" };
    const borders = { top: border, bottom: border, left: border, right: border };
    const headerBorder = { style: BorderStyle.SINGLE, size: 1, color: "185FA5" };
    const headerBorders = { top: headerBorder, bottom: headerBorder, left: headerBorder, right: headerBorder };
    const h1 = (text) => new Paragraph({ children: [new TextRun({ text, bold: true, size: 36, color: "185FA5", font: "Arial" })], spacing: { before: 0, after: 160 }, alignment: AlignmentType.CENTER });
    const h2 = (text) => new Paragraph({ children: [new TextRun({ text, bold: true, size: 24, color: "185FA5", font: "Arial" })], spacing: { before: 280, after: 100 }, border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: "185FA5" } } });
    const body = (text) => new Paragraph({ children: [new TextRun({ text: String(text || ""), size: 20, font: "Arial" })], spacing: { before: 60, after: 60 } });
    const bullet = (text) => new Paragraph({ children: [new TextRun({ text: String(text || ""), size: 20, font: "Arial" })], bullet: { level: 0 }, spacing: { before: 40, after: 40 } });
    const numbered = (text, i) => new Paragraph({ children: [new TextRun({ text: `${i + 1}. ${String(text || "")}`, size: 20, font: "Arial" })], spacing: { before: 40, after: 40 }, indent: { left: 360 } });
    const infoRow = (label, value) => value ? new Paragraph({ children: [new TextRun({ text: `${label}: `, bold: true, size: 20, font: "Arial" }), new TextRun({ text: String(value), size: 20, font: "Arial" })], spacing: { before: 40, after: 40 } }) : null;
    const divider = () => new Paragraph({ children: [new TextRun({ text: "", size: 4 })], border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" } }, spacing: { before: 120, after: 120 } });
    const tableCell = (text, isHeader = false) => new TableCell({ borders: isHeader ? headerBorders : borders, shading: { fill: isHeader ? "185FA5" : "FFFFFF", type: ShadingType.CLEAR }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: String(text || ""), size: 18, font: "Arial", bold: isHeader, color: isHeader ? "FFFFFF" : "111827" })] })] });
    const children = [];
    children.push(h1(result.course_title || "Course Syllabus"));
    const meta = [result.course_number && result.department ? `${result.course_number} | ${result.department}` : (result.course_number || result.department || null), result.credit_hours ? `${result.credit_hours} Credit Hours` : null, result.student_level || null].filter(Boolean);
    if (meta.length) children.push(new Paragraph({ children: [new TextRun({ text: meta.join("   ·   "), size: 18, color: "6B7280", font: "Arial", italics: true })], alignment: AlignmentType.CENTER, spacing: { before: 0, after: 40 } }));
    children.push(divider());
    children.push(h2("1. Course Information"));
    [["Course Title", result.course_title], ["Course Number", result.course_number], ["Department", result.department], ["Credit Hours", result.credit_hours], ["Student Level", result.student_level]].forEach(([label, value]) => { const p = infoRow(label, value); if (p) children.push(p); });
    if (result.course_description) { children.push(h2("2. Course Description")); children.push(body(result.course_description)); }
    if (result.learning_objectives?.length > 0) { children.push(h2("3. Learning Objectives")); children.push(body("Upon successful completion of this course, students will be able to:")); result.learning_objectives.forEach((obj, i) => children.push(numbered(obj, i))); }
    const hasMaterials = result.required_textbooks?.length || result.required_software?.length || result.additional_resources?.length;
    if (hasMaterials) { children.push(h2("4. Required Materials")); if (result.required_textbooks?.length > 0) { children.push(new Paragraph({ children: [new TextRun({ text: "Textbooks", bold: true, size: 20, font: "Arial" })], spacing: { before: 80, after: 40 } })); result.required_textbooks.forEach(t => children.push(bullet(t))); } if (result.required_software?.length > 0) { children.push(new Paragraph({ children: [new TextRun({ text: "Software & Tools", bold: true, size: 20, font: "Arial" })], spacing: { before: 80, after: 40 } })); result.required_software.forEach(s => children.push(bullet(s))); } if (result.additional_resources?.length > 0) { children.push(new Paragraph({ children: [new TextRun({ text: "Additional Resources", bold: true, size: 20, font: "Arial" })], spacing: { before: 80, after: 40 } })); result.additional_resources.forEach(r => children.push(bullet(r))); } }
    if (result.course_structure) { children.push(h2("5. Course Structure")); children.push(body(result.course_structure)); }
    if (result.grading?.length > 0) { children.push(h2("6. Assessments & Grading")); children.push(new Table({ width: { size: 5000, type: WidthType.DXA }, columnWidths: [3500, 1500], rows: [new TableRow({ children: [tableCell("Assessment Component", true), tableCell("Percentage", true)] }), ...result.grading.map(g => new TableRow({ children: [tableCell(g.component), tableCell(g.percentage)] })), new TableRow({ children: [new TableCell({ borders, shading: { fill: "F9FAFB", type: ShadingType.CLEAR }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: "Total", bold: true, size: 18, font: "Arial" })] })] }), new TableCell({ borders, shading: { fill: "F9FAFB", type: ShadingType.CLEAR }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: "100%", bold: true, size: 18, font: "Arial" })] })] })] })] })); if (result.grading_scale?.length > 0) { children.push(new Paragraph({ children: [new TextRun({ text: "Grading Scale", bold: true, size: 20, font: "Arial" })], spacing: { before: 160, after: 80 } })); const scaleRows = []; for (let i = 0; i < result.grading_scale.length; i += 3) { const chunk = result.grading_scale.slice(i, i + 3); const cells = []; chunk.forEach(g => { cells.push(new TableCell({ borders, margins: { top: 60, bottom: 60, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: `${g.grade}  ${g.range}`, size: 18, font: "Arial" })] })] })); }); while (cells.length < 3) cells.push(new TableCell({ borders, children: [new Paragraph({ children: [] })] })); scaleRows.push(new TableRow({ children: cells })); } children.push(new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [3120, 3120, 3120], rows: scaleRows })); } }
    const hasPolicies = result.assignment_policies || result.late_work_policy || result.collaboration_policy || result.ai_policy;
    if (hasPolicies) { children.push(h2("7. Assignment Policies")); if (result.assignment_policies) { children.push(new Paragraph({ children: [new TextRun({ text: "Submission Requirements", bold: true, size: 20, font: "Arial" })], spacing: { before: 80, after: 40 } })); children.push(body(result.assignment_policies)); } if (result.late_work_policy) { children.push(new Paragraph({ children: [new TextRun({ text: "Late Work Policy", bold: true, size: 20, font: "Arial" })], spacing: { before: 80, after: 40 } })); children.push(body(result.late_work_policy)); } if (result.collaboration_policy) { children.push(new Paragraph({ children: [new TextRun({ text: "Collaboration Policy", bold: true, size: 20, font: "Arial" })], spacing: { before: 80, after: 40 } })); children.push(body(result.collaboration_policy)); } if (result.ai_policy) { children.push(new Paragraph({ children: [new TextRun({ text: "Use of Artificial Intelligence Tools", bold: true, size: 20, font: "Arial" })], spacing: { before: 80, after: 40 } })); children.push(body(result.ai_policy)); } }
    if (result.attendance_policy) { children.push(h2("8. Attendance & Participation")); children.push(body(result.attendance_policy)); }
    if (result.weekly_schedule?.length > 0) { children.push(h2("9. Weekly Course Schedule")); children.push(new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [720, 2880, 2880, 2880], rows: [new TableRow({ children: ["Week", "Topics", "Readings / Resources", "Assignments / Assessments"].map(h => tableCell(h, true)) }), ...result.weekly_schedule.map((week, i) => new TableRow({ children: [new TableCell({ borders, shading: { fill: i % 2 === 0 ? "FFFFFF" : "F9FAFB", type: ShadingType.CLEAR }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: String(week.week || ""), bold: true, size: 18, color: "185FA5", font: "Arial" })] })] }), new TableCell({ borders, shading: { fill: i % 2 === 0 ? "FFFFFF" : "F9FAFB", type: ShadingType.CLEAR }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: String(week.topic || ""), size: 18, bold: true, font: "Arial" })] })] }), new TableCell({ borders, shading: { fill: i % 2 === 0 ? "FFFFFF" : "F9FAFB", type: ShadingType.CLEAR }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: String(week.readings || ""), size: 18, font: "Arial" })] })] }), new TableCell({ borders, shading: { fill: i % 2 === 0 ? "FFFFFF" : "F9FAFB", type: ShadingType.CLEAR }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, children: [new Paragraph({ children: [new TextRun({ text: String(week.assignments || ""), size: 18, font: "Arial" })] })] })] }))] })); }
    if (result.academic_integrity) { children.push(h2("10. Academic Integrity")); children.push(body(result.academic_integrity)); }
    if (result.accessibility) { children.push(h2("11. Accessibility & Accommodations")); children.push(body(result.accessibility)); }
    if (result.communication_policy) { children.push(h2("12. Communication Policy")); children.push(body(result.communication_policy)); }
    if (result.additional_info) { children.push(h2("13. Additional Course Information")); children.push(body(result.additional_info)); }
    children.push(divider());
    children.push(new Paragraph({ children: [new TextRun({ text: "Syllabus Modification Statement: ", bold: true, size: 18, font: "Arial", color: "6B7280" }), new TextRun({ text: "The instructor reserves the right to make reasonable modifications to this syllabus as necessary. Any changes will be communicated to students in a timely manner.", size: 18, font: "Arial", color: "6B7280", italics: true })], spacing: { before: 120, after: 0 } }));
    children.push(new Paragraph({ children: [new TextRun({ text: "Generated by SyllaPlus", size: 16, font: "Arial", color: "9CA3AF", italics: true })], alignment: AlignmentType.CENTER, spacing: { before: 120, after: 0 } }));
    const docName = `${(result.course_title || "syllabus").replace(/\s+/g, "_")}.docx`;
    const timestamp = new Date().toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }).replace(",", "");
    const doc = new Document({
      styles: { default: { document: { run: { font: "Arial", size: 20 } } } },
      sections: [{
        properties: { page: { size: { width: 12240, height: 15840 }, margin: { top: 1440, right: 1440, bottom: 1080, left: 1440 } } },
        headers: {
          default: new Header({
            children: [new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [new ImageRun({ data: docxLogoBytes, transformation: { width: 60, height: 60 }, type: "png" })]
            })]
          })
        },
        footers: {
          default: new Footer({
            children: [new Paragraph({
              tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
              children: [
                new TextRun({ text: `${docName}  ·  Generated: ${timestamp}`, size: 14, font: "Arial", color: "9CA3AF" }),
                new TextRun({ text: "\t", size: 14 }),
                new TextRun({ text: "Page ", size: 14, font: "Arial", color: "9CA3AF" }),
                new TextRun({ children: [PageNumber.CURRENT], size: 14, font: "Arial", color: "9CA3AF" }),
                new TextRun({ text: " of ", size: 14, font: "Arial", color: "9CA3AF" }),
                new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 14, font: "Arial", color: "9CA3AF" }),
              ]
            })]
          })
        },
        children
      }]
    });
    return await Packer.toBlob(doc);
  };

  const downloadRedesign = async (result) => {
    const blob = await downloadRedesignAsBlob(result);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(result.course_title || "syllabus").replace(/\s+/g, "_")}.docx`;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); window.URL.revokeObjectURL(url);
  };

  const downloadRedesignPDF = async (result) => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
    const blue = [24, 95, 165]; const gray = [107, 114, 128]; const dark = [17, 24, 39];
    const lightGray = [249, 250, 251]; const lineGray = [229, 231, 235];
    const pageW = doc.internal.pageSize.getWidth(); const pageH = doc.internal.pageSize.getHeight();
    const margin = 72; const contentW = pageW - margin * 2; let y = margin;
    const docName = `${(result.course_title || "syllabus").replace(/\s+/g, "_")}.pdf`;
    const timestamp = new Date().toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }).replace(",", "");
    const drawHeader = () => { const logoSize = 54; const logoX = pageW - margin - logoSize; const logoY = margin - 36; try { doc.addImage(SYLLAPLUS_LOGO_B64, "PNG", logoX, logoY, logoSize, logoSize); } catch (e) { } };
    const drawFooter = () => { const footerY = pageH - 36; doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.5); doc.line(margin, footerY - 10, pageW - margin, footerY - 10); doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(156, 163, 175); doc.text(`${docName}  ·  Generated: ${timestamp}`, margin, footerY); const pageNum = doc.internal.getCurrentPageInfo().pageNumber; doc.text(`Page ${pageNum}`, pageW - margin, footerY, { align: "right" }); };
    drawHeader(); drawFooter(); y = margin + 24;
    const checkPage = (needed = 20) => { if (y + needed > pageH - margin - 24) { doc.addPage(); drawHeader(); drawFooter(); y = margin + 24; } };
    const drawDivider = (color = lineGray) => { checkPage(16); doc.setDrawColor(...color); doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y); y += 12; };
    const writeH1 = (text) => { checkPage(40); doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...blue); doc.text(text, pageW / 2, y, { align: "center" }); y += 28; };
    const writeMeta = (text) => { checkPage(20); doc.setFont("helvetica", "italic"); doc.setFontSize(10); doc.setTextColor(...gray); doc.text(text, pageW / 2, y, { align: "center" }); y += 18; };
    const writeH2 = (text) => { checkPage(32); y += 8; doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...blue); doc.text(text, margin, y); y += 4; doc.setDrawColor(...blue); doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y); y += 12; };
    const writeBody = (text, indent = 0) => { if (!text) return; doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...dark); const lines = doc.splitTextToSize(String(text), contentW - indent); lines.forEach(line => { checkPage(14); doc.text(line, margin + indent, y); y += 14; }); y += 4; };
    const writeBullet = (text) => { doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...dark); const lines = doc.splitTextToSize(`• ${String(text)}`, contentW - 16); lines.forEach((line, i) => { checkPage(14); doc.text(line, margin + (i === 0 ? 0 : 12), y); y += 14; }); };
    const writeNumbered = (text, i) => { doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...dark); const lines = doc.splitTextToSize(`${i + 1}. ${String(text)}`, contentW - 16); lines.forEach((line, j) => { checkPage(14); doc.text(line, margin + (j === 0 ? 0 : 16), y); y += 14; }); };
    const writeInfoRow = (label, value) => { if (!value) return; checkPage(16); doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...dark); doc.text(`${label}: `, margin, y); const labelW = doc.getTextWidth(`${label}: `); doc.setFont("helvetica", "normal"); doc.text(String(value), margin + labelW, y); y += 16; };
    const writeSubheading = (text) => { checkPage(20); y += 6; doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...dark); doc.text(text, margin, y); y += 14; };
    const writeTable = (headers, rows, colWidths) => { const rowH = 22; checkPage(rowH + 8); let x = margin; doc.setFillColor(...blue); doc.rect(margin, y, contentW, rowH, "F"); doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(255, 255, 255); headers.forEach((h, i) => { doc.text(String(h), x + 6, y + 15); x += colWidths[i]; }); y += rowH; rows.forEach((row, ri) => { const cellLines = row.map((cell, ci) => doc.splitTextToSize(String(cell || ""), colWidths[ci] - 12)); const maxLines = Math.max(...cellLines.map(l => l.length)); const rh = Math.max(rowH, maxLines * 13 + 10); checkPage(rh); if (ri % 2 === 1) { doc.setFillColor(...lightGray); doc.rect(margin, y, contentW, rh, "F"); } doc.setDrawColor(...lineGray); doc.setLineWidth(0.3); doc.rect(margin, y, contentW, rh); doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...dark); let cx = margin; cellLines.forEach((lines, ci) => { if (ci === 0) { doc.setFont("helvetica", "bold"); doc.setTextColor(...blue); } else { doc.setFont("helvetica", "normal"); doc.setTextColor(...dark); } lines.forEach((line, li) => { doc.text(line, cx + 6, y + 14 + li * 13); }); cx += colWidths[ci]; }); y += rh; }); y += 8; };
    writeH1(result.course_title || "Course Syllabus");
    const meta = [result.course_number && result.department ? `${result.course_number} | ${result.department}` : (result.course_number || result.department || null), result.credit_hours ? `${result.credit_hours} Credit Hours` : null, result.student_level].filter(Boolean);
    if (meta.length) writeMeta(meta.join("   ·   "));
    drawDivider(blue);
    writeH2("1. Course Information");
    [["Course Title", result.course_title], ["Course Number", result.course_number], ["Department", result.department], ["Credit Hours", result.credit_hours], ["Student Level", result.student_level]].forEach(([l, v]) => writeInfoRow(l, v));
    if (result.course_description) { writeH2("2. Course Description"); writeBody(result.course_description); }
    if (result.learning_objectives?.length > 0) { writeH2("3. Learning Objectives"); writeBody("Upon successful completion of this course, students will be able to:"); result.learning_objectives.forEach((o, i) => writeNumbered(o, i)); }
    if (result.required_textbooks?.length || result.required_software?.length || result.additional_resources?.length) { writeH2("4. Required Materials"); if (result.required_textbooks?.length) { writeSubheading("Textbooks"); result.required_textbooks.forEach(writeBullet); } if (result.required_software?.length) { writeSubheading("Software & Tools"); result.required_software.forEach(writeBullet); } if (result.additional_resources?.length) { writeSubheading("Additional Resources"); result.additional_resources.forEach(writeBullet); } }
    if (result.course_structure) { writeH2("5. Course Structure"); writeBody(result.course_structure); }
    if (result.grading?.length > 0) { writeH2("6. Assessments & Grading"); writeTable(["Assessment Component", "Percentage"], [...result.grading.map(g => [g.component, g.percentage]), ["Total", "100%"]], [contentW * 0.7, contentW * 0.3]); if (result.grading_scale?.length > 0) { writeSubheading("Grading Scale"); const scaleRows = []; for (let i = 0; i < result.grading_scale.length; i += 3) { const chunk = result.grading_scale.slice(i, i + 3); while (chunk.length < 3) chunk.push({ grade: "", range: "" }); scaleRows.push(chunk.map(g => `${g.grade}  ${g.range}`)); } writeTable(["", "", ""], scaleRows, [contentW / 3, contentW / 3, contentW / 3]); } }
    if (result.assignment_policies || result.late_work_policy || result.collaboration_policy || result.ai_policy) { writeH2("7. Assignment Policies"); if (result.assignment_policies) { writeSubheading("Submission Requirements"); writeBody(result.assignment_policies); } if (result.late_work_policy) { writeSubheading("Late Work Policy"); writeBody(result.late_work_policy); } if (result.collaboration_policy) { writeSubheading("Collaboration Policy"); writeBody(result.collaboration_policy); } if (result.ai_policy) { writeSubheading("Use of Artificial Intelligence Tools"); writeBody(result.ai_policy); } }
    if (result.attendance_policy) { writeH2("8. Attendance & Participation"); writeBody(result.attendance_policy); }
    if (result.weekly_schedule?.length > 0) { writeH2("9. Weekly Course Schedule"); writeTable(["Week", "Topics", "Readings / Resources", "Assignments / Assessments"], result.weekly_schedule.map(w => [String(w.week || ""), w.topic || "", w.readings || "", w.assignments || ""]), [contentW * 0.08, contentW * 0.32, contentW * 0.30, contentW * 0.30]); }
    if (result.academic_integrity) { writeH2("10. Academic Integrity"); writeBody(result.academic_integrity); }
    if (result.accessibility) { writeH2("11. Accessibility & Accommodations"); writeBody(result.accessibility); }
    if (result.communication_policy) { writeH2("12. Communication Policy"); writeBody(result.communication_policy); }
    if (result.additional_info) { writeH2("13. Additional Course Information"); writeBody(result.additional_info); }
    drawDivider();
    doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...gray);
    doc.text("The instructor reserves the right to make reasonable modifications to this syllabus as necessary. Any changes will be communicated to students in a timely manner.", margin, y, { maxWidth: contentW });
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) { doc.setPage(p); const footerY = pageH - 36; doc.setFillColor(255, 255, 255); doc.rect(pageW - margin - 80, footerY - 10, 80, 16, "F"); doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(156, 163, 175); doc.text(`Page ${p} of ${totalPages}`, pageW - margin, footerY, { align: "right" }); }
    doc.save(docName);
  };

  // ── File Manager ──
  const handleUpdateTag = async (fileId, newTag) => {
    await fetch(`${API}/files/${fileId}/tag`, { method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ tag: newTag }) });
    fetchFiles();
  };

  const handleStarFile = async (fileId) => {
    await fetch(`${API}/files/${fileId}/star`, { method: "PUT", headers: authHeaders });
    fetchFiles();
  };

  const handleToggleTagLock = async (fileId) => {
    await fetch(`${API}/files/${fileId}/tag-lock`, { method: "PUT", headers: authHeaders });
    fetchFiles();
  };

  const fetchRelatedFiles = async (fileId) => {
    setShowRelatedPanel(fileId);
    setLoadingRelated(true);
    setRelatedFiles([]);
    try {
      const res = await fetch(`${API}/files/${fileId}/related`, { headers: authHeaders });
      const data = await res.json();
      setRelatedFiles(data.related || []);
    } catch {
      setRelatedFiles([]);
    }
    setLoadingRelated(false);
  };

  const handleBackfillRelationships = async () => {
    setBackfillingRelationships(true);
    setBackfillMessage("");
    try {
      const res = await fetch(`${API}/files/backfill-relationships`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      setBackfillMessage(data.message || "Scan complete");
    } catch {
      setBackfillMessage("Something went wrong. Please try again.");
    }
    setBackfillingRelationships(false);
  };

  const handleAskFiles = async (scopeToFolder) => {
    if (!askQuestion.trim()) return;
    setAskLoading(true);
    setAskError("");
    setAskAnswer(null);
    try {
      const res = await fetch(`${API}/files/ask`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ question: askQuestion, folder_id: scopeToFolder ? activeFolderId : null })
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        const message = typeof detail === "string" ? detail : Array.isArray(detail) ? (detail[0]?.msg || "Failed to get an answer") : "Failed to get an answer";
        setAskError(message);
      } else {
        setAskAnswer(data);
      }
    } catch {
      setAskError("Something went wrong. Please try again.");
    }
    setAskLoading(false);
  };

  const handleSummarizeFile = async (fileId) => {
    setSummarizingFileIds(prev => [...prev, fileId]);
    setSummarizeMessage("");
    try {
      const res = await fetch(`${API}/files/${fileId}/summarize`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) {
        setSummarizeMessage(data.detail || "Failed to summarize file");
        setSummarizingFileIds(prev => prev.filter(id => id !== fileId));
        return;
      }
      // Summary generates in the background — poll once after a delay, then stop showing "summarizing"
      setTimeout(async () => {
        await fetchFiles();
        setSummarizingFileIds(prev => prev.filter(id => id !== fileId));
      }, 5000);
    } catch {
      setSummarizeMessage("Something went wrong. Please try again.");
      setSummarizingFileIds(prev => prev.filter(id => id !== fileId));
    }
  };

  const handleSummarizeAll = async () => {
    setSummarizingAll(true);
    setSummarizeMessage("");
    try {
      const res = await fetch(`${API}/files/summarize-all`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) {
        setSummarizeMessage(data.detail || "Failed to start summarization");
        setSummarizingAll(false);
        return;
      }
      setSummarizeMessage(data.message || "Summarization started");
      setTimeout(async () => {
        await fetchFiles();
        setSummarizingAll(false);
      }, 8000);
    } catch {
      setSummarizeMessage("Something went wrong. Please try again.");
      setSummarizingAll(false);
    }
  };

  const handleRetagFile = async (fileId, filename) => {
    setRetaggingFileIds(prev => [...prev, fileId]);
    setRetagMessage("");
    try {
      const res = await fetch(`${API}/files/${fileId}/retag`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) {
        setRetagMessage(data.detail || "Failed to re-tag file");
      } else {
        setRetagMessage(data.changed ? `✓ "${filename}": ${data.old_tag} → ${data.tag}` : `No change — already tagged ${data.tag}`);
        await fetchFiles();
      }
    } catch {
      setRetagMessage("Something went wrong. Please try again.");
    }
    setRetaggingFileIds(prev => prev.filter(id => id !== fileId));
  };

  const handleRetagAll = async () => {
    setRetaggingAll(true);
    setRetagMessage("");
    setRetagChanges(null);
    try {
      const res = await fetch(`${API}/files/retag-all`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) {
        setRetagMessage(data.detail || "Failed to re-tag files");
        setRetaggingAll(false);
        return;
      }
      setRetagMessage(data.message);
      setRetagChanges(data.changes || []);
      await fetchFiles();
    } catch {
      setRetagMessage("Something went wrong. Please try again.");
    }
    setRetaggingAll(false);
  };

  const handleCopyFile = (fileId) => setCopiedFileId(fileId);

  const handlePasteFile = async (folderId) => {
    if (!copiedFileId) return;
    await handleAssignFilesToFolder(folderId, [copiedFileId]);
    setCopiedFileId(null);
  };

  const handleAcceptSuggestion = async (suggestion) => {
    await handleAssignFilesToFolder(suggestion.folder_id, [suggestion.file_id]);
    setFolderSuggestions(prev => prev.filter(s => s.file_id !== suggestion.file_id));
  };

  const handleDismissSuggestion = (fileId) => {
    setFolderSuggestions(prev => prev.filter(s => s.file_id !== fileId));
  };

  // ── Folders ──
  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API}/folders`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setFolders(data.folders || []);
    } catch { setFolders([]); }
  };

  const fetchFolderSuggestions = async () => {
    if (folders.length === 0) return;
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`${API}/folders/suggest`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ file_ids: [] }) });
      const data = await res.json();
      setFolderSuggestions(data.suggestions || []);
    } catch { setFolderSuggestions([]); }
    setLoadingSuggestions(false);
  };

  const fetchFolderDefaults = async () => {
    try {
      const res = await fetch(`${API}/folders/defaults`, { headers: authHeaders });
      const data = await res.json();
      setNewCourseSubfolders(data.defaults || []); setFolderDefaults(data.defaults || []);
    } catch {}
  };

  const handleCreateCourse = async () => {
    if (!newCourseName.trim()) return;
    setCreatingCourse(true);
    try {
      const res = await fetch(`${API}/folders/course`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ name: newCourseName, subfolders: newCourseSubfolders }) });
      if (res.ok) { await fetchFolders(); setShowNewCourseForm(false); setNewCourseName(""); setNewCourseSubfolders(folderDefaults); }
    } catch {}
    setCreatingCourse(false);
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm("Move this folder (and everything inside it) to Trash?")) return;
    await fetch(`${API}/folders/${folderId}`, { method: "DELETE", headers: authHeaders });
    await fetchFolders();
    if (activeFolderId === folderId) setActiveFolderId(null);
  };

  const handleRenameFolder = async (folderId, newName) => {
    await fetch(`${API}/folders/${folderId}`, { method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ name: newName }) });
    await fetchFolders();
  };

  const handleAssignFilesToFolder = async (folderId, fileIds) => {
    await fetch(`${API}/folders/${folderId}/files`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ file_ids: fileIds }) });
    await fetchFolders();
  };

  const handleRemoveFileFromFolder = async (folderId, fileId) => {
    await fetch(`${API}/folders/${folderId}/files/${fileId}`, { method: "DELETE", headers: authHeaders });
    await fetchFolders();
  };

  // ── Groups ──
  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await fetch(`${API}/groups`, { headers: authHeaders });
      const data = await res.json();
      setGroups(data.groups || []);
    } catch {
      setGroups([]);
    }
    setLoadingGroups(false);
  };

  const fetchPendingInvites = async () => {
    try {
      const res = await fetch(`${API}/groups/pending-invites`, { headers: authHeaders });
      const data = await res.json();
      setPendingInvites(data.invites || []);
    } catch {
      setPendingInvites([]);
    }
  };

  const fetchGroupMembers = async (groupId) => {
    try {
      const res = await fetch(`${API}/groups/${groupId}/members`, { headers: authHeaders });
      const data = await res.json();
      setGroupMembers(data.members || []);
    } catch {
      setGroupMembers([]);
    }
  };

  const handleCreateGroup = async (name) => {
    if (!name.trim()) return;
    setCreatingGroup(true);
    setGroupError("");
    try {
      const res = await fetch(`${API}/groups`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (!res.ok) {
        setGroupError(data.detail || "Failed to create group");
      } else {
        await fetchGroups();
      }
    } catch {
      setGroupError("Something went wrong. Please try again.");
    }
    setCreatingGroup(false);
  };

  const handleInviteMember = async (groupId) => {
    if (!inviteEmail.trim()) return;
    setInvitingMember(true);
    setGroupError("");
    try {
      const res = await fetch(`${API}/groups/${groupId}/invite`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        setGroupError(data.detail || "Failed to send invite");
      } else {
        setInviteEmail("");
        await fetchGroupMembers(groupId);
      }
    } catch {
      setGroupError("Something went wrong. Please try again.");
    }
    setInvitingMember(false);
  };

  const handleAcceptInvite = async (groupId) => {
    await fetch(`${API}/groups/${groupId}/accept`, { method: "POST", headers: authHeaders });
    await fetchPendingInvites();
    await fetchGroups();
  };

  const handleDeclineInvite = async (groupId) => {
    await fetch(`${API}/groups/${groupId}/decline`, { method: "POST", headers: authHeaders });
    await fetchPendingInvites();
  };

  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm("Are you sure you want to leave this group?")) return;
    const res = await fetch(`${API}/groups/${groupId}/leave`, { method: "POST", headers: authHeaders });
    const data = await res.json();
    if (!res.ok) {
      setGroupError(data.detail || "Failed to leave group");
    } else {
      setActiveGroup(null);
      await fetchGroups();
    }
  };

  const handlePromoteMember = async (groupId, userId) => {
    const res = await fetch(`${API}/groups/${groupId}/members/${userId}/promote`, { method: "POST", headers: authHeaders });
    const data = await res.json();
    if (!res.ok) setGroupError(data.detail || "Failed to promote member");
    else await fetchGroupMembers(groupId);
  };

  const handleRemoveMember = async (groupId, userId, name) => {
    if (!window.confirm(`Remove ${name} from this group?`)) return;
    const res = await fetch(`${API}/groups/${groupId}/members/${userId}/remove`, { method: "POST", headers: authHeaders });
    const data = await res.json();
    if (!res.ok) {
      setGroupError(data.detail || "Failed to remove member");
    } else {
      await fetchGroupMembers(groupId);
      await fetchGroups();
    }
  };

  const fetchGroupPosts = async (groupId) => {
    setLoadingPosts(true);
    try {
      const res = await fetch(`${API}/groups/${groupId}/posts`, { headers: authHeaders });
      const data = await res.json();
      setGroupPosts(data.posts || []);
    } catch {
      setGroupPosts([]);
    }
    setLoadingPosts(false);
  };

  const handleCreatePost = async (groupId, title, file) => {
    if (!title.trim()) return;
    setCreatingPost(true);
    setPostError("");
    try {
      const formData = new FormData();
      formData.append("title", title);
      if (file) formData.append("file", file);
      const res = await fetch(`${API}/groups/${groupId}/posts`, { method: "POST", headers: authHeaders, body: formData });
      const data = await res.json();
      if (!res.ok) {
        setPostError(data.detail || "Failed to create post");
      } else {
        await fetchGroupPosts(groupId);
      }
    } catch {
      setPostError("Something went wrong. Please try again.");
    }
    setCreatingPost(false);
  };

  const fetchPostDetail = async (groupId, postId) => {
    setLoadingPostDetail(true);
    try {
      const res = await fetch(`${API}/groups/${groupId}/posts/${postId}`, { headers: authHeaders });
      const data = await res.json();
      setPostDetail(res.ok ? data : null);
    } catch {
      setPostDetail(null);
    }
    setLoadingPostDetail(false);
  };

  const clearPostDetail = () => setPostDetail(null);

  const handleAddComment = async (groupId, postId, content, file, versionOf) => {
    if (!content.trim() && !file) return;
    setAddingComment(true);
    setCommentError("");
    try {
      const formData = new FormData();
      formData.append("content", content);
      if (file) formData.append("file", file);
      if (versionOf) formData.append("version_of", versionOf);
      const res = await fetch(`${API}/groups/${groupId}/posts/${postId}/comments`, { method: "POST", headers: authHeaders, body: formData });
      const data = await res.json();
      if (!res.ok) {
        setCommentError(data.detail || "Failed to add comment");
      } else {
        await fetchPostDetail(groupId, postId);
        await fetchGroupPosts(groupId);
      }
    } catch {
      setCommentError("Something went wrong. Please try again.");
    }
    setAddingComment(false);
  };

  const fetchSharedFiles = async (groupId) => {
    setLoadingSharedFiles(true);
    try {
      const res = await fetch(`${API}/groups/${groupId}/shared-files`, { headers: authHeaders });
      const data = await res.json();
      setSharedFiles(data.files || []);
    } catch {
      setSharedFiles([]);
    }
    setLoadingSharedFiles(false);
  };

  const fetchGroupFolders = async (groupId) => {
    try {
      const res = await fetch(`${API}/groups/${groupId}/folders`, { headers: authHeaders });
      const data = await res.json();
      setGroupFolders(data.folders || []);
    } catch {
      setGroupFolders([]);
    }
  };

  const handleCreateGroupFolder = async (groupId, name) => {
    if (!name.trim()) return;
    try {
      const res = await fetch(`${API}/groups/${groupId}/folders/course`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ name, subfolders: [] })
      });
      const data = await res.json();
      if (!res.ok) {
        setSharedFilesMessage(data.detail || "Failed to create folder");
      } else {
        await fetchGroupFolders(groupId);
      }
    } catch {
      setSharedFilesMessage("Something went wrong. Please try again.");
    }
  };

  const handleAssignSharedFileFolder = async (groupId, fileId, folderId) => {
    try {
      const res = await fetch(`${API}/groups/${groupId}/shared-files/${fileId}/folder`, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ folder_id: folderId })
      });
      if (res.ok) {
        await fetchSharedFiles(groupId);
        await fetchGroupFolders(groupId);
      }
    } catch {}
  };

  const handleCopyToMyFiles = async (fileId, filename) => {
    setCopyingFileId(fileId);
    setSharedFilesMessage("");
    try {
      const res = await fetch(`${API}/files/${fileId}/copy-to-my-files`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) {
        setSharedFilesMessage(data.detail || "Failed to copy file");
      } else {
        setSharedFilesMessage(`✓ "${filename}" copied to My Files`);
        await fetchFiles();
      }
    } catch {
      setSharedFilesMessage("Something went wrong. Please try again.");
    }
    setCopyingFileId(null);
  };

  const handleSummarizeSharedFile = async (groupId, fileId) => {
    setSummarizingSharedFileIds(prev => [...prev, fileId]);
    setSharedFilesMessage("");
    try {
      const res = await fetch(`${API}/files/${fileId}/summarize`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) {
        setSharedFilesMessage(data.detail || "Failed to summarize file");
        setSummarizingSharedFileIds(prev => prev.filter(id => id !== fileId));
        return;
      }
      setTimeout(async () => {
        await fetchSharedFiles(groupId);
        setSummarizingSharedFileIds(prev => prev.filter(id => id !== fileId));
      }, 5000);
    } catch {
      setSharedFilesMessage("Something went wrong. Please try again.");
      setSummarizingSharedFileIds(prev => prev.filter(id => id !== fileId));
    }
  };

  const handleDeleteSharedFile = async (groupId, fileId, filename) => {
    if (!window.confirm(`Delete "${filename}" for the entire group? This cannot be undone.`)) return;
    setDeletingSharedFileId(fileId);
    setSharedFilesMessage("");
    try {
      const res = await fetch(`${API}/groups/${groupId}/shared-files/${fileId}`, { method: "DELETE", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) {
        setSharedFilesMessage(data.detail || "Failed to delete file");
      } else {
        setSharedFilesMessage(data.message || "File deleted");
        await fetchSharedFiles(groupId);
      }
    } catch {
      setSharedFilesMessage("Something went wrong. Please try again.");
    }
    setDeletingSharedFileId(null);
  };

  const handleDownloadSharedZip = async (groupId, folderId, label) => {
    setDownloadingSharedZip(true);
    try {
      const params = folderId ? `?folder_id=${folderId}` : "";
      const res = await fetch(`${API}/groups/${groupId}/shared-files/download-zip${params}`, { headers: authHeaders });
      if (!res.ok) {
        const data = await res.json();
        setSharedFilesMessage(data.detail || "Failed to download files");
        setDownloadingSharedZip(false);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${label || "shared_files"}.zip`;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); window.URL.revokeObjectURL(url);
    } catch {
      setSharedFilesMessage("Something went wrong. Please try again.");
    }
    setDownloadingSharedZip(false);
  };

  // ── Calendar ──
  const fetchTerms = async () => {
    try {
      const res = await fetch(`${API}/terms`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const termList = data.terms || [];
      setTerms(termList);
      if (termList.length > 0) fetchAllLectures(termList);
    } catch { setTerms([]); }
  };

  const fetchLectures = async (termId) => {
    try { const res = await fetch(`${API}/terms/${termId}/lectures`, { headers: authHeaders }); const data = await res.json(); setLectures(data.lectures || []); } catch { setLectures([]); }
  };

  const fetchAllLectures = async (termList) => {
    try {
      const results = await Promise.all((termList || terms).map(t => fetch(`${API}/terms/${t.id}/lectures`, { headers: authHeaders }).then(r => r.json()).then(d => (d.lectures || []).map(l => ({ ...l, term_id: t.id })))));
      setAllLectures(results.flat());
    } catch { setAllLectures([]); }
  };

  const handleCreateTerm = async () => {
    if (!termForm.course_name.trim() || !termForm.start_date || !termForm.end_date || termForm.lecture_days.length === 0) return;
    setTermLoading(true);
    try {
      const res = await fetch(`${API}/terms`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify(termForm) });
      if (res.ok) { await fetchTerms(); setShowTermForm(false); setTermForm({ course_name: "", term_name: "", start_date: "", end_date: "", lecture_days: [], lecture_time_start: "10:00", lecture_time_end: "11:15" }); }
    } catch {}
    setTermLoading(false);
  };

  const handleDeleteTerm = async (termId) => {
    if (!window.confirm("Delete this term and all its lecture dates?")) return;
    await fetch(`${API}/terms/${termId}`, { method: "DELETE", headers: authHeaders });
    setTerms(prev => prev.filter(t => t.id !== termId));
    if (activeTerm?.id === termId) { setActiveTerm(null); setLectures([]); }
  };

  const handleSelectTerm = async (term) => {
    setActiveTerm(term); await fetchLectures(term.id);
    setCalendarMonth(new Date(term.start_date));
  };

  const handleEditTerm = (term) => {
    setEditingTerm(term.id);
    setEditTermForm({ course_name: term.course_name, term_name: term.term_name, start_date: term.start_date, end_date: term.end_date, lecture_days: term.lecture_days, lecture_time_start: term.lecture_time_start, lecture_time_end: term.lecture_time_end });
  };

  const handleSaveEditTerm = async () => {
    if (!editingTerm) return;
    setEditTermLoading(true);
    try {
      const res = await fetch(`${API}/terms/${editingTerm}`, { method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify(editTermForm) });
      if (res.ok) { await fetchTerms(); if (activeTerm?.id === editingTerm) { await fetchLectures(editingTerm); setCalendarMonth(new Date(editTermForm.start_date)); } setEditingTerm(null); setEditTermForm({}); }
    } catch {}
    setEditTermLoading(false);
  };

  const handleDuplicateTerm = async (termId) => {
    try {
      const res = await fetch(`${API}/terms/${termId}/duplicate`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (res.ok) { const termsRes = await fetch(`${API}/terms`, { headers: authHeaders }); const termsData = await termsRes.json(); setTerms(termsData.terms || []); const newTerm = (termsData.terms || []).find(t => t.id === data.term_id); if (newTerm) handleEditTerm(newTerm); }
    } catch {}
  };

  const handleSelectLecture = (lecture) => {
    setSelectedLecture(lecture);
    setLectureEdit({ topic: lecture.topic || "", notes: lecture.notes || "", file_ids: lecture.file_ids || [], date_type: lecture.date_type || "lecture", status: lecture.status || "scheduled", cancel_note: lecture.cancel_note || "" });
  };

  const handleSaveLecture = async () => {
    if (!selectedLecture) return;
    setSavingLecture(true);
    try {
      await fetch(`${API}/lectures/${selectedLecture.id}`, { method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify(lectureEdit) });
      setLectures(prev => prev.map(l => l.id === selectedLecture.id ? { ...l, ...lectureEdit } : l));
      setAllLectures(prev => prev.map(l => l.id === selectedLecture.id ? { ...l, ...lectureEdit } : l));
      setSelectedLecture(prev => ({ ...prev, ...lectureEdit }));
    } catch {}
    setSavingLecture(false);
  };

  const handleRescheduleLecture = async (newDate, newTimeStart, newTimeEnd) => {
    if (!selectedLecture) return;
    setSavingLecture(true);
    try {
      const res = await fetch(`${API}/lectures/${selectedLecture.id}/reschedule`, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify({ new_date: newDate, new_time_start: newTimeStart, new_time_end: newTimeEnd }) });
      if (res.ok) { await fetchAllLectures(terms); }
    } catch {}
    setSavingLecture(false);
  };

  const toggleLectureFile = (fileId) => setLectureEdit(prev => ({ ...prev, file_ids: prev.file_ids.includes(fileId) ? prev.file_ids.filter(id => id !== fileId) : [...prev.file_ids, fileId] }));
  const toggleLectureDay = (day) => setTermForm(prev => ({ ...prev, lecture_days: prev.lecture_days.includes(day) ? prev.lecture_days.filter(d => d !== day) : [...prev.lecture_days, day] }));

  const handleApplySyllabus = async () => {
    const termId = applySyllabusTermId || activeTerm?.id;
    if (!termId || (!applySyllabusFolder && !applySyllabusFile)) return;
    setApplyingSyllabus(true); setApplyMessage("");
    try {
      const endpoint = applySyllabusFolder ? `${API}/terms/apply-folder` : `${API}/terms/apply-syllabus`;
      const body = applySyllabusFolder ? { folder_id: parseInt(applySyllabusFolder), term_id: parseInt(termId), syllabus_file_id: applySyllabusFile ? parseInt(applySyllabusFile) : null } : { file_id: parseInt(applySyllabusFile), term_id: parseInt(termId) };
      const res = await fetch(endpoint, { method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to apply syllabus");
      setApplyMessage(`✓ Calendar built — ${data.weeks_mapped} weeks mapped from ${data.files_analyzed} files${data.used_structured_syllabus ? " using your saved syllabus" : " (tip: save a syllabus from Syllabus Studio for better results)"}`);
      await fetchAllLectures(terms); setShowApplySyllabus(false);
    } catch (err) { setApplyMessage(err.message || "Something went wrong."); }
    setApplyingSyllabus(false);
  };

  const strengthColors = { weak: "#ef4444", fair: "#f97316", good: "#eab308", strong: "#22c55e" };
  const strengthWidths = { weak: "25%", fair: "50%", good: "75%", strong: "100%" };

  return (
    <AppContext.Provider value={{
      // Theme
      themeMode, setThemeMode, colorTheme, setColorTheme,
      // Profile
      firstName, setFirstName, lastName, setLastName, currentUserId,
      // Auth
      token, setToken, authView, setAuthView, email, setEmail, password, setPassword,
      authError, setAuthError, passwordStrength, setPasswordStrength,
      handleSignup, handleLogin, handleForgotPassword, handleResetPassword, handleLogout, validatePassword, getPasswordStrength,
      isVerified, handleVerifyEmail, handleResendVerification, resendingVerification, verificationMessage, handleConfirmEmailChange, setVerificationMessage,
      strengthColors, strengthWidths,
      // Files
      files, setFiles, selectedFiles, setSelectedFiles, uploading, uploadMessage, uploadProgress,
      fileView, setFileView, fetchFiles, handleUpload, handleIncomingFiles, processUpload,
      handleDelete, handleBulkDelete, handleDownloadAll, handleBulkDownload,
      toggleSelectFile, toggleSelectResult,
      // Search
      query, setQuery, results, setResults, searched, setSearched,
      selectedResults, setSelectedResults, suggestions, setSuggestions,
      showSuggestions, setShowSuggestions, activeTagFilter, setActiveTagFilter,
      handleSearch, fetchSuggestions,
      // Drive
      driveAccounts, activeDriveAccountId, setActiveDriveAccountId, driveFiles, importingDrive, driveMessage, setDriveMessage,
      checkDriveStatus, fetchDriveFiles, connectGoogleDrive, importDriveFile, disconnectDriveAccount,
      // Redesign
      redesignFiles, setRedesignFiles, redesignParams, setRedesignParams,
      redesignResult, setRedesignResult, redesignLoading, redesignError, setRedesignError,
      refineInput, setRefineInput, refineLoading, savingRedesign,
      applyToTermId, setApplyToTermId, applyingToCalendar,
      redesignHistory, setRedesignHistory, redesignHistoryLoading,
      savingToFolder, setSavingToFolder, saveToFolderMessage, setSaveToFolderMessage,
      sessionHistory, setSessionHistory, sessionIndex, setSessionIndex,
      activeHistoryEntryId, setActiveHistoryEntryId,
      handleRedesign, handleRefine, toggleRedesignFile,
      loadRedesignHistory, fetchRedesignHistoryEntry, deleteRedesignHistoryEntry,
      handleSaveSyllabusToFolder, handleSaveRedesign, handleApplyRedesignToCalendar,
      downloadRedesign, downloadRedesignAsBlob, downloadRedesignPDF,
      // Folders
      folders, setFolders, activeFolderId, setActiveFolderId,
      showNewCourseForm, setShowNewCourseForm, newCourseName, setNewCourseName,
      newCourseSubfolders, setNewCourseSubfolders, creatingCourse, folderDefaults,
      fetchFolders, fetchFolderDefaults, fetchFolderSuggestions,
      handleCreateCourse, handleDeleteFolder, handleRenameFolder,
      handleAssignFilesToFolder, handleRemoveFileFromFolder,
      sortBy, setSortBy, copiedFileId, handleCopyFile, handlePasteFile,
      showUploadModal, setShowUploadModal, pendingFiles, setPendingFiles,
      uploadTargetFolder, setUploadTargetFolder,
      showNewFolderInModal, setShowNewFolderInModal,
      newFolderNameInModal, setNewFolderNameInModal,
      duplicateWarning, resolveDuplicate, cancelDuplicateUpload,
      folderSuggestions, setFolderSuggestions, loadingSuggestions,
      handleAcceptSuggestion, handleDismissSuggestion,
      handleUpdateTag, handleStarFile, handleToggleTagLock,
      fetchRelatedFiles, relatedFiles, loadingRelated, showRelatedPanel, setShowRelatedPanel,
      handleBackfillRelationships, backfillingRelationships, backfillMessage, setBackfillMessage,
      askQuestion, setAskQuestion, askAnswer, setAskAnswer, askLoading, askError, setAskError, handleAskFiles,
      previewFile, setPreviewFile,
      trashedFiles, trashedFolders, loadingTrash, trashMessage, setTrashMessage,
      fetchTrash, handleRestoreFile, handlePermanentlyDeleteFile, handleRestoreFolder, handlePermanentlyDeleteFolder,
      selectedTrashFiles, selectedTrashFolders, toggleSelectTrashFile, toggleSelectTrashFolder,
      handleBulkRestoreFiles, handleBulkPermanentDeleteFiles, handleBulkRestoreFolders, handleBulkPermanentDeleteFolders,
      clipboard, pasteCollision, pasteFolderChoice, setPasteFolderChoice, clipboardMessage, setClipboardMessage,
      clipboardCopyFiles, clipboardCutFiles, clipboardCopyFolder, clipboardCutFolder,
      performFilePaste, resolvePasteCollision, initiateFolderPaste, resolveFolderPasteChoice,
      showGapAnalysisModal, gapAnalysisStep, gapAnalysisFiles, gapAnalysisSelectedIds, gapAnalysisCourseFolderName,
      loadingGapAnalysisFiles, gapAnalysisSyllabusFiles, loadingSyllabusFiles, gapAnalysisSyllabusFileId, setGapAnalysisSyllabusFileId,
      gapAnalysisResult, runningGapAnalysis, gapAnalysisError, setGapAnalysisError,
      openGapAnalysisModal, closeGapAnalysisModal, toggleGapAnalysisFile,
      goToGapAnalysisStep2, backToGapAnalysisStep1, runGapAnalysis,
      handleSummarizeFile, handleSummarizeAll,
      summarizingFileIds, summarizingAll, summarizeMessage, setSummarizeMessage,
      handleRetagFile, handleRetagAll,
      retaggingFileIds, retaggingAll, retagMessage, setRetagMessage,
      retagChanges, setRetagChanges,
      // Calendar
      terms, activeTerm, setActiveTerm, allLectures, lectures, setLectures,
      showTermForm, setShowTermForm, termForm, setTermForm, termLoading,
      editingTerm, setEditingTerm, editTermForm, setEditTermForm, editTermLoading,
      selectedLecture, lectureEdit, setLectureEdit, calendarMonth, setCalendarMonth,
      savingLecture, showApplySyllabus, setShowApplySyllabus,
      applySyllabusTermId, setApplySyllabusTermId,
      applySyllabusFile, setApplySyllabusFile,
      applySyllabusFolder, setApplySyllabusFolder,
      applyingSyllabus, applyMessage, setApplyMessage,
      fetchTerms, fetchLectures, fetchAllLectures,
      handleCreateTerm, handleDeleteTerm, handleSelectTerm,
      handleEditTerm, handleSaveEditTerm, handleDuplicateTerm,
      handleSelectLecture, handleSaveLecture, handleRescheduleLecture,
      toggleLectureFile, toggleLectureDay, handleApplySyllabus,
      // Groups
      groups, pendingInvites, activeGroup, setActiveGroup, groupMembers,
      loadingGroups, creatingGroup, groupError, setGroupError,
      inviteEmail, setInviteEmail, invitingMember,
      fetchGroups, fetchPendingInvites, fetchGroupMembers,
      handleCreateGroup, handleInviteMember, handleAcceptInvite, handleDeclineInvite, handleLeaveGroup,
      handlePromoteMember, handleRemoveMember,
      groupPosts, loadingPosts, creatingPost, postError, setPostError,
      fetchGroupPosts, handleCreatePost,
      postDetail, loadingPostDetail, addingComment, commentError, setCommentError,
      fetchPostDetail, clearPostDetail, handleAddComment,
      sharedFiles, loadingSharedFiles, groupFolders, copyingFileId, sharedFilesMessage, setSharedFilesMessage,
      fetchSharedFiles, fetchGroupFolders, handleCreateGroupFolder, handleAssignSharedFileFolder, handleCopyToMyFiles,
      summarizingSharedFileIds, deletingSharedFileId, downloadingSharedZip,
      handleSummarizeSharedFile, handleDeleteSharedFile, handleDownloadSharedZip,
    }}>
      {children}
    </AppContext.Provider>
  );
}